import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  fetchNotifications,
  updateNotificationStatus,
  NOTIFICATIONS_PAGE_SIZE,
} from '../../services/notification'
import { getLogger } from '../../core/debuggers'

const logger = getLogger('Notification.slice.js')

const initialState = {
  notifications: [],
  page: 0,
  pageSize: NOTIFICATIONS_PAGE_SIZE,
  total: 0,
  status: {
    fetch: 'idle', // idle | loading | succeeded | failed
    update: 'idle',
  },
  error: null,
}

export const getNotifications = createAsyncThunk(
  'notification/fetch',
  async (page = 0, { getState, rejectWithValue }) => {
    try {
      const state = getState()
      const userId = state.adminAuth.user?.id
      logger.debug('fetch notifications for user', userId)
      if (!userId) return rejectWithValue('User not authenticated')
      return await fetchNotifications(userId, page, NOTIFICATIONS_PAGE_SIZE)
    } catch (error) {
      logger.error(error)
      return rejectWithValue(error.message)
    }
  }
)

export const markNotificationAsRead = createAsyncThunk(
  'notification/markRead',
  async (id, { getState, rejectWithValue }) => {
    try {
      const state = getState()
      const userId = state.adminAuth.user?.id
      if (!userId) return rejectWithValue({ id, message: 'User not authenticated' })
      return await updateNotificationStatus(id, 'read', userId)
    } catch (error) {
      logger.error(error)
      return rejectWithValue({ id, message: error.message })
    }
  }
)

const notificationsSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    resetNotificationsState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(getNotifications.pending, (state, action) => {
        state.status.fetch = 'loading'
        state.error = null
        // Clear only on first page so pagination does not flash empty,
        // and a user switch (page 0 refetch) cannot show the previous user's rows.
        const page = action.meta.arg ?? 0
        if (page === 0) {
          state.notifications = []
          state.total = 0
        }
      })
      .addCase(getNotifications.fulfilled, (state, action) => {
        const { data, total, page, pageSize } = action.payload || {}
        state.notifications = data || []
        state.total = total ?? 0
        state.page = page ?? 0
        state.pageSize = pageSize ?? NOTIFICATIONS_PAGE_SIZE
        state.status.fetch = 'succeeded'
        logger.debug(action.payload)
      })
      .addCase(getNotifications.rejected, (state, action) => {
        state.status.fetch = 'failed'
        state.error = action.payload
      })

      .addCase(markNotificationAsRead.pending, (state, action) => {
        state.status.update = 'loading'
        const id = action.meta.arg
        const notif = state.notifications.find((n) => n.id === id)
        if (notif) notif.status = 'read'
      })
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        state.status.update = 'succeeded'
        const updated = action.payload
        const idx = state.notifications.findIndex((n) => n.id === updated?.id)
        if (idx !== -1 && updated) {
          state.notifications[idx] = { ...state.notifications[idx], ...updated }
        }
      })
      .addCase(markNotificationAsRead.rejected, (state, action) => {
        state.status.update = 'failed'
        state.error = action.payload?.message || action.payload
        const id = action.payload?.id ?? action.meta.arg
        const notif = state.notifications.find((n) => n.id === id)
        if (notif) notif.status = 'unread'
      })
  }
})

export const { resetNotificationsState } = notificationsSlice.actions

export default notificationsSlice.reducer
